// Copyright (c) Microsoft. All rights reserved.

import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setNotifications } from '../../../../redux/features/app/appSlice';
import { copyUrlToClipboard } from '../../../utils/CopyUtils';
import { ModalDialog } from '../ChatWindow/Shared/ModalDialog';
import { NOTIFICATION_TEMPLATES } from '../Notifications/NotificationConstants';
import { useChat } from '../../../../libs/hooks';

interface ShareChatDialogProps {
    chatId: string;
    open: boolean;
    onClose: () => void;
}

export const ShareChatDialog: React.FC<ShareChatDialogProps> = ({ chatId, open, onClose }) => {
    const dispatch = useDispatch();
    const [isIdCopied, setIsIdCopied] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [joinChatCode, setJoinChatCode] = React.useState<string>("");
    const chat = useChat();

    // Copy the chatId to clipboard by default when component mounts.
    useEffect(() => {
        void copyUrlToClipboard(chatId, setIsIdCopied);
    }, [chatId]);

    useEffect(() => {
        if (open && !joinChatCode && !isLoading) {
            const fetchJoinChatCode = async () => {
                setIsLoading(true);
                try {
                    const result = await chat.getJoinChatCode(chatId);
                    if (result.success && result.joinCode) {
                        setJoinChatCode(result.joinCode);
                    }
                } finally {
                    setIsLoading(false);
                }
            };
            void fetchJoinChatCode();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, chatId]);

    return (
        <ModalDialog title="Invite others to your chat" onClose={onClose} open={open}>
            <div className="mb-6">Please provide the following Join Code to your colleagues so they can join the chat.</div>
            <div className="mb-4 flex items-center border border-gray-600 p-2 rounded-md dark:bg-slate-700">
                <div className="relative w-full">
                    <input
                        className="w-full bg-transparent pr-4 p-2 focus:outline focus:outline-blue-300 rounded-md"
                        value={joinChatCode}
                        readOnly
                    />
                    <div className="absolute top-0 right-0 h-full w-20 pointer-events-none bg-gradient-to-r rounded-r-md from-transparent dark:to-slate-700 to-white"></div>
                </div>
                <button
                    type="button"
                    data-autofocus
                    onClick={() => {
                        void copyUrlToClipboard(joinChatCode, setIsIdCopied);
                        dispatch(setNotifications([{ ...NOTIFICATION_TEMPLATES.COPY_CHATID }]));
                        onClose();
                    }}
                    className="inline-flex items-center ml-2 w-60 cursor-pointer justify-center rounded-lg py-2 text-white bg-primary-600 hover:bg-primary-700 dark:text-slate-50 transition-all duration-300">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke={isIdCopied ? '#fff' : 'currentColor'}
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M8 8m0 2a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2z"></path>
                        <path d="M16 8v-2a2 2 0 0 0 -2 -2h-8a2 2 0 0 0 -2 2v8a2 2 0 0 0 2 2h2"></path>
                    </svg>
                    <span className="ml-2">Copy ID</span>
                </button>
            </div>
        </ModalDialog>
    );
};
